import React, { useState } from 'react';
import './CreateEventModal.css';
import supabase from "../../../supabaseClient";
import { useAuth } from '../../../contexts/authContext';

const CreateEventModal = ({ onClose, onCreate }) => {
  const { user } = useAuth();
  const [form, setForm] = useState({
    title: '',
    date: '',
    time: '',
    language: '',
    level: '',
    description: '',
    event_banner_url: ''
  });
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = e => {
    const file = e.target.files[0];
    if (!file) return;
    setImage(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleRemoveImage = () => {
    setImage(null);
    setImagePreview(null);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setSubmitting(true);
    let event_banner_url = '';
    try {
      if (image) {
        const fileExt = image.name.split('.').pop();
        const fileName = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}.${fileExt}`;
        const filePath = `event-banners/${fileName}`;
        const { error: uploadError } = await supabase.storage
          .from('banners')
          .upload(filePath, image);
        if (uploadError) throw uploadError;
        event_banner_url = filePath; // Store only the relative path
      }
      const { data: newEvent, error: eventError } = await supabase
        .from('events')
        .insert([
          {
            title: form.title,
            date: form.date,
            time: form.time,
            language: form.language,
            level: form.level || null,
            description: form.description,
            event_banner_url,
            created_by: user.id,
            created_at: new Date().toISOString()
          }
        ])
        .select()
        .single();
      if (eventError || !newEvent) throw eventError || new Error('Failed to create event');

      // After event creation, create a corresponding Virtual Cafe room and link it
      try {
        const { data: newRoom, error: roomError } = await supabase
          .from('rooms')
          .insert([
            {
              name: form.title,
              language: form.language,
              // level will be wired once events have a CEFR level column
              created_by: user.id,
              event_id: newEvent.id
            }
          ])
          .select()
          .single();

        if (!roomError && newRoom) {
          await supabase
            .from('events')
            .update({ room_id: newRoom.id })
            .eq('id', newEvent.id);
        }
      } catch (linkError) {
        console.error('Error creating linked Virtual Cafe room for event:', linkError);
      }
      if (onCreate) onCreate(form, image);
      onClose();
    } catch (err) {
      alert('Error creating event: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="create-event-modal">
        <button className="close-btn" onClick={onClose}>×</button>
        <h2>Create Event</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Title</label>
            <input name="title" value={form.title} onChange={handleChange} required />
          </div>
          <div className="form-group">
            <label>Date</label>
            <input type="date" name="date" value={form.date} onChange={handleChange} required />
          </div>
          <div className="form-group">
            <label>Time</label>
            <input type="time" name="time" value={form.time} onChange={handleChange} required />
          </div>
          <div className="form-group">
            <label>Language</label>
            <select name="language" value={form.language} onChange={handleChange} required>
              <option value="">Select Language</option>
              <option value="English">English</option>
              <option value="Spanish">Spanish</option>
              <option value="French">French</option>
              <option value="German">German</option>
              <option value="Chinese">Chinese</option>
              <option value="Japanese">Japanese</option>
            </select>
          </div>
          <div className="form-group">
            <label>Level (CEFR)</label>
            <select name="level" value={form.level} onChange={handleChange}>
              <option value="">All / Mixed levels</option>
              <option value="A1">A1 (Beginner)</option>
              <option value="A2">A2 (Elementary)</option>
              <option value="B1">B1 (Intermediate)</option>
              <option value="B2">B2 (Upper Intermediate)</option>
              <option value="C1">C1 (Advanced)</option>
              <option value="C2">C2 (Proficient)</option>
              <option value="Mixed">Mixed</option>
            </select>
          </div>
          <div className="form-group">
            <label>Event Image</label>
            <input type="file" accept="image/*" onChange={handleImageChange} />
            {imagePreview && (
              <div className="image-preview-container">
                <img src={imagePreview} alt="Preview" />
                <button type="button" className="remove-image-btn" onClick={handleRemoveImage}>Remove</button>
              </div>
            )}
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea name="description" value={form.description} onChange={handleChange} rows="4" required />
          </div>
          <div className="form-actions">
            <button type="submit" className="create-btn" disabled={submitting}>{submitting ? 'Creating...' : 'Create'}</button>
            <button type="button" className="cancel-btn" onClick={onClose}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateEventModal;